import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  TrendingUp,
  RotateCcw,
  ChevronLeft,
  Award,
  Target,
  Lightbulb,
  RefreshCw,
  User,
} from 'lucide-react';
import type { SalaryMessage } from '../types/salaryNegotiation';
import type { SalaryNegotiationConfig } from '../services/salaryAIService';
import type { BehaviorSummary } from '../types';
import { logo } from '../assets/images';
import { authFetch } from '../services/auth';

// 添加缺失的导入
import { App } from 'antd';

interface ReportState {
  report: {
    result: {
      finalOffer: number;
      isAccepted: boolean;
      totalRounds: number;
      userPerformance: {
        strategy: number;
        communication: number;
        timing: number;
        flexibility: number;
      };
    };
    analysis: {
      strengths: string[];
      weaknesses: string[];
      keyMoments: Array<{
        round: number;
        description: string;
        suggestion: string;
      }>;
    };
    suggestions: Array<{
      category: string;
      content: string;
    }>;
    marketComparison: {
      userExpected: number;
      marketMedian: number;
      finalOffer: number;
      percentile: number;
    };
    behaviorAnalysis?: BehaviorSummary;
  };
  dialogueHistory: SalaryMessage[];
  salaryConfig: SalaryNegotiationConfig;
  finalOffer?: number;
  callDuration?: number;
  behaviorSummary?: BehaviorSummary;
}

interface SalaryNegotiationDetail {
  id: number;
  jobType: string;
  jobLevel: string;
  dialogueHistory: SalaryMessage[];
  report: ReportState['report'];
  finalOffer: number;
  isAccepted: number;
  totalRounds: number;
  score: number;
  duration: number;
  createdAt: number;
}

// 格式化时长
function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

export default function SalaryReportPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detailData, setDetailData] = useState<SalaryNegotiationDetail | null>(
    null,
  );

  // 从 location.state 获取数据（直接谈薪后跳转）
  const stateData = (location.state as ReportState) || {};
  const {
    report: stateReport,
    dialogueHistory: stateDialogueHistory,
    salaryConfig: stateSalaryConfig,
    finalOffer: stateFinalOffer,
    callDuration: stateCallDuration,
    behaviorSummary: stateBehaviorSummary,
  } = stateData;

  // 如果有 ID，从后端获取详情
  useEffect(() => {
    if (id) {
      fetchSalaryNegotiationDetail(id);
    }
  }, [id]);

  // 进入页面时滚动到顶部
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fetchSalaryNegotiationDetail = async (negotiationId: string) => {
    setLoading(true);
    setError('');
    try {
      const response = await authFetch(
        `${import.meta.env.VITE_API_BASE_URL || ''}/api/salary-negotiations/${negotiationId}`,
      );
      if (!response.ok) {
        throw new Error('获取谈薪详情失败');
      }
      const data = await response.json();
      setDetailData(data);
    } catch (err) {
      setError('获取谈薪详情失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 确定数据来源
  const report = stateReport || detailData?.report;
  const dialogueHistory =
    stateDialogueHistory || detailData?.dialogueHistory || [];
  const salaryConfig = stateSalaryConfig;
  const finalOffer = stateFinalOffer || detailData?.finalOffer;
  const callDuration = stateCallDuration || detailData?.duration;
  const behaviorSummary =
    stateBehaviorSummary || detailData?.report?.behaviorAnalysis;
  const jobType =
    detailData?.jobType || salaryConfig?.interviewConfig?.jobType || '';
  const jobLevel =
    detailData?.jobLevel || salaryConfig?.interviewConfig?.level || '';

  // 判断是否从历史记录进入（有ID表示从历史记录进入）
  const fromHistory = !!id;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0B0F19] via-[#0F172A] to-[#1E293B] flex items-center justify-center p-4 pt-24">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">加载中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0B0F19] via-[#0F172A] to-[#1E293B] flex items-center justify-center p-4 pt-24">
        <div className="text-center">
          <p className="text-slate-400 mb-4">{error}</p>
          <button
            onClick={() => id && fetchSalaryNegotiationDetail(id)}
            className="px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-xl hover:from-amber-400 hover:to-amber-500 transition-all cursor-pointer"
          >
            重新加载
          </button>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0B0F19] via-[#0F172A] to-[#1E293B] flex items-center justify-center p-4 pt-24">
        <div className="text-center">
          <p className="text-slate-400 mb-4">暂无谈薪报告</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-xl hover:from-amber-400 hover:to-amber-500 transition-all cursor-pointer"
          >
            返回首页
          </button>
        </div>
      </div>
    );
  }

  const { result, analysis, suggestions } = report;

  // 计算平均表现分
  const avgPerformance = Math.round(
    (result.userPerformance.strategy +
      result.userPerformance.communication +
      result.userPerformance.timing +
      result.userPerformance.flexibility) /
      4,
  );

  // 根据评分获取颜色和等级信息 - 与简历评估结果页保持一致
  const getScoreInfo = (score: number) => {
    if (score >= 90) {
      return {
        color: '#22c55e',
        gradient: 'from-green-400 via-green-500 to-green-600',
        label: '卓越',
        glow: 'shadow-green-500/30',
        textColor: 'text-green-400',
      };
    }
    if (score >= 80) {
      return {
        color: '#f59e0b',
        gradient: 'from-amber-400 via-amber-500 to-amber-600',
        label: '优秀',
        glow: 'shadow-amber-500/30',
        textColor: 'text-amber-400',
      };
    }
    if (score >= 70) {
      return {
        color: '#eab308',
        gradient: 'from-yellow-400 via-yellow-500 to-yellow-600',
        label: '良好',
        glow: 'shadow-yellow-500/30',
        textColor: 'text-yellow-400',
      };
    }
    if (score >= 60) {
      return {
        color: '#f97316',
        gradient: 'from-orange-400 via-orange-500 to-orange-600',
        label: '及格',
        glow: 'shadow-orange-500/30',
        textColor: 'text-orange-400',
      };
    }
    return {
      color: '#ef4444',
      gradient: 'from-red-400 via-red-500 to-red-600',
      label: '待提升',
      glow: 'shadow-red-500/30',
      textColor: 'text-red-400',
    };
  };

  // 根据分数获取进度条颜色 - 与h5端保持一致
  const getScoreBarColor = (score: number) => {
    if (score >= 90) return 'bg-green-500';
    if (score >= 80) return 'bg-amber-500';
    if (score >= 70) return 'bg-yellow-500';
    if (score >= 60) return 'bg-orange-500';
    return 'bg-red-500';
  };

  // 行为表现指标颜色 - higherIsBetter=true 时数值越高越好, false 时越低越好
  const getBehaviorColor = (value: number, higherIsBetter = true) => {
    if (higherIsBetter) {
      if (value >= 80) return 'text-emerald-400';
      if (value >= 60) return 'text-amber-400';
      return 'text-red-400';
    }
    if (value <= 30) return 'text-emerald-400';
    if (value <= 60) return 'text-amber-400';
    return 'text-red-400';
  };

  const scoreInfo = getScoreInfo(avgPerformance);

  // 行为表现 AI 建议 - 优先使用接口返回的 behaviorAnalysis.suggestions
  const behaviorSuggestions = (
    (report as any)?.behaviorAnalysis?.suggestions as string[] | undefined
  )?.length
    ? ((report as any).behaviorAnalysis.suggestions as string[])
    : behaviorSummary
      ? (() => {
          const list = [];
          if (behaviorSummary.averageConfidence < 60) {
            list.push(
              '谈薪时自信度偏低，建议提前准备好期望薪资和市场数据，增强底气',
            );
          }
          if (behaviorSummary.averageNervousness > 50) {
            list.push(
              '紧张度较高，建议在谈薪前进行深呼吸放松，把谈薪看作双向选择而非请求',
            );
          }
          if (behaviorSummary.eyeContactRate < 0.5) {
            list.push(
              '眼神接触较少，建议在表达关键诉求时直视对方，传达诚恳和自信',
            );
          }
          if (behaviorSummary.smileFrequency < 0.3) {
            list.push('微笑频率偏低，适当微笑有助于营造轻松的谈判氛围');
          }
          if (behaviorSummary.postureStats.armsCrossedRatio > 0.3) {
            list.push('抱臂比例较高，注意保持开放姿态，避免传递防御性信号');
          }
          if (behaviorSummary.postureStats.forwardLeanRatio < 0.2) {
            list.push('身体前倾较少，适当前倾可显示积极投入的态度');
          }
          if (list.length === 0) {
            list.push('谈薪整体行为表现优秀，继续保持！');
          }
          return list;
        })()
      : [];

  // 表现维度配置
  const performanceDimensions = [
    {
      label: '策略运用',
      score: result.userPerformance.strategy,
    },
    {
      label: '沟通表达',
      score: result.userPerformance.communication,
    },
    {
      label: '时机把握',
      score: result.userPerformance.timing,
    },
    {
      label: '灵活应变',
      score: result.userPerformance.flexibility,
    },
  ];

  const handleBack = () => {
    navigate('/history');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B0F19] via-[#0F172A] to-[#1E293B] p-4 md:p-8 pt-24">
      <div className="max-w-6xl mx-auto" style={{ marginTop: '60px' }}>
        {/* 返回按钮 - 从历史记录进入时显示 */}
        {fromHistory && (
          <button
            onClick={handleBack}
            className="mb-0 flex items-center gap-2 text-slate-400 hover:text-white transition-colors cursor-pointer"
          >
            <ChevronLeft className="w-5 h-5" />
            <span style={{ position: 'relative', left: '-5px' }}>返回</span>
          </button>
        )}

        {/* 顶部装饰 */}
        <div className="relative mb-8">
          <div className="absolute inset-0 bg-gradient-to-r from-amber-600/20 via-emerald-600/20 to-amber-600/20 blur-3xl" />
          <div className="relative text-center pt-0 pb-8">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-2xl overflow-hidden mb-4">
              <img
                src={logo}
                alt="面面帮"
                className="w-full h-full object-contain"
              />
            </div>
            <h1 className="text-4xl font-bold text-white mb-2">
              面面帮 · 谈薪复盘报告
            </h1>
            <p className="text-slate-400">
              {jobType} · {jobLevel} · 薪酬沟通
            </p>
          </div>
        </div>

        {/* 总分卡片 */}
        <div className="relative bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-3xl p-8 mb-6 border border-slate-700/50 shadow-2xl">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 via-emerald-500 to-amber-500 rounded-t-3xl" />

          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            {/* 左侧分数 */}
            <div className="flex items-center gap-6">
              <div className="relative" style={{ width: 128, height: 128 }}>
                {/* 外发光效果 */}
                <div
                  className={`absolute inset-0 rounded-full blur-md opacity-50 bg-gradient-to-br ${scoreInfo.gradient}`}
                />

                <svg
                  className="w-full h-full transform -rotate-90 relative z-10"
                  style={{ width: 128, height: 128 }}
                >
                  {/* 背景圆环 */}
                  <circle
                    cx={64}
                    cy={64}
                    r={60}
                    stroke="#1E293B"
                    strokeWidth={8}
                    fill="none"
                  />
                  {/* 进度圆环 */}
                  <circle
                    cx={64}
                    cy={64}
                    r={60}
                    stroke={scoreInfo.color}
                    strokeWidth={8}
                    fill="none"
                    strokeDasharray={2 * Math.PI * 60}
                    strokeDashoffset={
                      2 * Math.PI * 60 -
                      (avgPerformance / 100) * 2 * Math.PI * 60
                    }
                    strokeLinecap="round"
                    className="transition-all duration-1000 ease-out"
                  />
                </svg>

                {/* 中心内容 */}
                <div className="absolute inset-0 flex flex-col items-center justify-center z-20">
                  <span
                    className={`text-4xl font-bold leading-none ${scoreInfo.textColor}`}
                  >
                    {Math.ceil(avgPerformance)}
                  </span>
                  <span className="text-xs text-slate-400 mt-1">综合评分</span>
                </div>
              </div>
              <div>
                <div
                  className={`text-2xl font-bold bg-gradient-to-r ${scoreInfo.gradient} bg-clip-text text-transparent mb-1`}
                >
                  {scoreInfo.label}
                </div>
                <p className="text-slate-400 text-sm">本次谈薪表现评估</p>
              </div>
            </div>

            {/* 右侧核心结果 */}
            <div className="flex-1 grid grid-cols-2 md:grid-cols-3 gap-4 w-full">
              <div className="bg-slate-800/50 rounded-xl p-4 text-center border border-slate-700/30">
                <span className="text-amber-400 text-xl font-bold mx-auto mb-2 block">
                  ¥
                </span>
                <div className="text-2xl font-bold text-white mb-1">
                  {result.isAccepted
                    ? `${finalOffer || result.finalOffer}k`
                    : '-'}
                </div>
                <div className="text-xs text-slate-400">最终offer</div>
                <div
                  className={`text-xs mt-1 ${result.isAccepted ? 'text-emerald-400' : 'text-red-400'}`}
                >
                  {result.isAccepted ? '已达成' : '未达成'}
                </div>
              </div>
              <div className="bg-slate-800/50 rounded-xl p-4 text-center border border-slate-700/30">
                <RefreshCw className="w-5 h-5 text-amber-400 mx-auto mb-2" />
                <div className="text-2xl font-bold text-white mb-1">
                  {result.totalRounds}
                </div>
                <div className="text-xs text-slate-400">谈判轮次</div>
              </div>
              <div className="bg-slate-800/50 rounded-xl p-4 text-center border border-slate-700/30">
                <Target className="w-5 h-5 text-amber-400 mx-auto mb-2" />
                <div className="text-2xl font-bold text-white mb-1">
                  {result.marketComparison?.percentile || 60}%
                </div>
                <div className="text-xs text-slate-400">市场分位</div>
              </div>
            </div>
          </div>
        </div>

        {/* 表现评分 */}
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/30 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
              <Award className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-white">表现评分</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {performanceDimensions.map((item) => (
              <div
                key={item.label}
                className="bg-slate-900/50 rounded-xl p-4 text-center border border-slate-700/30"
              >
                <div
                  className={`text-2xl font-bold mb-1 ${getScoreInfo(item.score).textColor}`}
                >
                  {item.score}
                </div>
                <div className="text-xs text-slate-400">{item.label}</div>
                <div className="mt-2 h-1 bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-1000 ${getScoreBarColor(item.score)}`}
                    style={{ width: `${item.score}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 行为表现分析 */}
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/30 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-white">行为表现分析</h2>
          </div>

          {behaviorSummary ? (
            <>
              {/* 四个核心指标 */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-slate-900/50 rounded-xl p-4 text-center border border-slate-700/30">
                  <div
                    className={`text-2xl font-bold mb-1 ${getBehaviorColor(behaviorSummary.averageConfidence)}`}
                  >
                    {behaviorSummary.averageConfidence}
                  </div>
                  <div className="text-xs text-slate-400">平均自信度</div>
                </div>
                <div className="bg-slate-900/50 rounded-xl p-4 text-center border border-slate-700/30">
                  <div
                    className={`text-2xl font-bold mb-1 ${getBehaviorColor(behaviorSummary.averageNervousness, false)}`}
                  >
                    {behaviorSummary.averageNervousness}
                  </div>
                  <div className="text-xs text-slate-400">平均紧张度</div>
                </div>
                <div className="bg-slate-900/50 rounded-xl p-4 text-center border border-slate-700/30">
                  <div
                    className={`text-2xl font-bold mb-1 ${getBehaviorColor(Math.round(behaviorSummary.eyeContactRate * 100))}`}
                  >
                    {Math.round(behaviorSummary.eyeContactRate * 100)}%
                  </div>
                  <div className="text-xs text-slate-400">眼神接触率</div>
                </div>
                <div className="bg-slate-900/50 rounded-xl p-4 text-center border border-slate-700/30">
                  <div
                    className={`text-2xl font-bold mb-1 ${getBehaviorColor(behaviorSummary.averageEngagement)}`}
                  >
                    {behaviorSummary.averageEngagement}
                  </div>
                  <div className="text-xs text-slate-400">平均投入度</div>
                </div>
              </div>

              {/* 详细指标 */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/30">
                  <h3 className="text-emerald-400 font-semibold text-sm mb-3">
                    面部表情
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">微笑频率</span>
                      <span
                        className={getBehaviorColor(
                          Math.round(behaviorSummary.smileFrequency * 100),
                        )}
                      >
                        {Math.round(behaviorSummary.smileFrequency * 100)}%
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">看向镜头</span>
                      <span className="text-white">
                        {behaviorSummary.gazeStats.lookingAtCamera} 帧
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">看向别处</span>
                      <span className="text-white">
                        {behaviorSummary.gazeStats.lookingAway} 帧
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">低头</span>
                      <span className="text-white">
                        {behaviorSummary.gazeStats.lookingDown} 帧
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/30">
                  <h3 className="text-emerald-400 font-semibold text-sm mb-3">
                    身体姿态
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">前倾占比</span>
                      <span
                        className={getBehaviorColor(
                          Math.round(
                            behaviorSummary.postureStats.forwardLeanRatio * 100,
                          ),
                        )}
                      >
                        {Math.round(
                          behaviorSummary.postureStats.forwardLeanRatio * 100,
                        )}
                        %
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">抱臂占比</span>
                      <span
                        className={getBehaviorColor(
                          Math.round(
                            behaviorSummary.postureStats.armsCrossedRatio * 100,
                          ),
                          false,
                        )}
                      >
                        {Math.round(
                          behaviorSummary.postureStats.armsCrossedRatio * 100,
                        )}
                        %
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">坐姿稳定性</span>
                      <span
                        className={getBehaviorColor(
                          Math.round(
                            behaviorSummary.postureStats.averageStability * 100,
                          ),
                        )}
                      >
                        {Math.round(
                          behaviorSummary.postureStats.averageStability * 100,
                        )}
                        %
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 行为表现建议 */}
              {behaviorSuggestions.length > 0 && (
                <div className="mt-4 bg-gradient-to-r from-emerald-600/10 via-emerald-600/10 to-emerald-600/10 border border-emerald-500/20 rounded-xl p-4">
                  <div className="space-y-1.5">
                    {behaviorSuggestions.map((suggestion, i) => (
                      <p key={i} className="text-emerald-300 text-sm">
                        💡 {suggestion}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-slate-500">
              <User className="w-12 h-12 mb-3 text-slate-600" />
              <p className="text-sm">本次谈薪未采集到行为表现数据</p>
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* 优势分析 */}
          <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/30">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-white">你的优势</h2>
            </div>
            <ul className="space-y-3">
              {analysis.strengths.map((strength: string, index: number) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-sm font-bold">
                    {index + 1}
                  </span>
                  <span className="text-slate-300 text-sm leading-relaxed">
                    {strength}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* 改进建议 */}
          <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/30">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
                <Lightbulb className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-white">提升建议</h2>
            </div>
            <div className="space-y-4">
              {suggestions.map(
                (
                  suggestion: { category: string; content: string },
                  index: number,
                ) => (
                  <div
                    key={index}
                    className="bg-slate-900/50 rounded-xl p-4 border-l-4 border-amber-500"
                  >
                    <h3 className="text-amber-400 font-semibold text-sm mb-2">
                      {suggestion.category}
                    </h3>
                    <p className="text-slate-300 text-sm leading-relaxed">
                      {suggestion.content}
                    </p>
                  </div>
                ),
              )}
            </div>
          </div>
        </div>

        {/* 关键节点 */}
        {analysis.keyMoments && analysis.keyMoments.length > 0 && (
          <div className="mt-6 bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/30">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                <Target className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-white">关键节点</h2>
            </div>
            <div className="space-y-4">
              {analysis.keyMoments.map((moment, index) => (
                <div
                  key={index}
                  className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/30"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center text-sm font-bold">
                      {moment.round}
                    </span>
                    <span className="text-amber-400 font-semibold text-sm">
                      {moment.description}
                    </span>
                  </div>
                  <p className="text-slate-300 text-sm">{moment.suggestion}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 底部提示 */}
        <div className="mt-6 bg-gradient-to-r from-amber-600/10 via-emerald-600/10 to-amber-600/10 border border-amber-500/20 rounded-xl p-4">
          <p className="text-amber-300 text-sm text-center">
            💡 提示：本报告由 AI
            智能生成，仅供参考。谈薪是一门艺术，持续练习是提升谈判能力的关键！
          </p>
        </div>

        {/* 操作按钮 - 从历史记录进入时隐藏 */}
        {!fromHistory && salaryConfig && (
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
            <button
              onClick={() =>
                navigate('/loading', {
                  state: {
                    mode: 'salary',
                    interviewConfig: salaryConfig.interviewConfig,
                    report: {
                      totalScore: salaryConfig.overallScore,
                      dimensions: {
                        overall: salaryConfig.overallScore,
                        technical: 0,
                        communication: 0,
                        expression: 0,
                        logic: 0,
                        professionalism: 0,
                        learning: 0,
                      },
                      strengths: [],
                      suggestions: [],
                      detailedAnalysis: {
                        expression: '',
                        technical: '',
                        logic: '',
                        communication: '',
                        professionalism: '',
                        learning: '',
                      },
                      nextSteps: [],
                    },
                  },
                })
              }
              className="w-[300px] py-4 bg-gradient-to-r from-emerald-600 via-emerald-500 to-emerald-400 hover:from-emerald-500 hover:via-emerald-400 hover:to-emerald-300
                text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              <RotateCcw className="w-5 h-5" />
              再来一次谈薪
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
